import React from "react";
import type { FC } from "react";
import UtilityPanel from "../layout/UtilityPanel";
import CorePanelFooter from "../layout/CorePanelFooter";
import CoreLayout from "../layout/CoreLayout";
import Button from "../form/Button";
import { useAppStore } from "../../store/appStore";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import classnames from "classnames";
import { submitCredentials } from "../../api/api";
import type { SubmitCredentialsResponse } from "../../api/dev-data/submitCredentials";
import { withErrorBoundary } from "../components/withErrorBoundary";
import * as Sentry from '@sentry/react'

interface LoginFormValues {
	username: string;
	password: string;
	agreement: boolean;
}

const validationSchema = Yup.object().shape({
	username: Yup.string()
		.email("Please enter a valid email address")
		.required("Username is required"),
	password: Yup.string()
		.min(6, "Password must be at least 6 characters")
		.max(32, "Password must not exceed 32 characters")
		.matches(/^[^~%<> ]*$/, "Password cannot contain ~ % < > or spaces")
		.required("Password is required"),
	agreement: Yup.boolean()
		.oneOf([true], "You must agree to the terms to continue")
		.required("Agreement is required")
});

const Login: FC = () => {
	const { setSessionStatus, setForceCheckSessionStatus } = useAppStore();

	const initialValues: LoginFormValues = {
		username: "",
		password: "",
		agreement: false
	};

	function onNext() {
		setForceCheckSessionStatus(true);
	}

	function onPrev() {
		setSessionStatus('INITIALIZED')
	}

	return (
		<CoreLayout>
			<div className="w-full max-w-3xl mx-auto grid gap-5">
				<Formik
					initialValues={initialValues}
					validationSchema={validationSchema}
					onSubmit={async (values) => {
						console.log(values);
						const response: SubmitCredentialsResponse = await submitCredentials(values.username, values.password)
						console.log(response);
						
						// Store session data in sessionStorage if login was successful
						if (response.success && response.sessionId) {
							const sessionData = {
								sessionId: response.sessionId,
								createdAt: new Date(),
								expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000) // 24 hours from now
							};
							sessionStorage.setItem('session', JSON.stringify(sessionData));
							console.info({ login: 'session stored', sessionId: response.sessionId });
                            Sentry.setUser({
                                id: response.sessionId,
                            })
						}
						
						onNext();
					}}
				>
					{({ errors, touched, setFieldValue, isValid, dirty }) => (
						<Form>
							<UtilityPanel title="Please provide your Pacific Gas and Electric (PG&E) account credentials">
								<div className="w-full space-y-4">
									<div className="w-full">
										<div className="text-black text-sm font-normal font-['Inter']">
											PG&E Username
										</div>
										<Field
											name="username"
											type="text"
											className={classnames(
												"w-full h-11 px-2 text-sm bg-white rounded-[5px] border focus:outline outline-2",
												{
													"border-pge-blue focus:outline-pge-blue": !touched.username || !errors.username,
													"border-king-red focus:outline-king-red": touched.username && errors.username
												}
											)}
											placeholder="e.g. jdoe@acme-inc.com"
										/>
										{errors.username && touched.username && (
											<div className="text-king-red text-xs font-normal font-['Inter'] mt-1">
												{errors.username}
											</div>
										)}
									</div>
									<div className="w-full">
										<div className="text-black text-sm font-normal font-['Inter']">
											PG&E Password
										</div>
										<Field
											name="password"
											type="password"
											className={classnames(
												"w-full h-11 px-2 text-sm bg-white rounded-[5px] border focus:outline outline-2",
												{
													"border-pge-blue focus:outline-pge-blue": !touched.password || !errors.password,
													"border-king-red focus:outline-king-red": touched.password && errors.password
												}
											)}
										/>
										{errors.password && touched.password && (
											<div className="text-king-red text-xs font-normal font-['Inter'] mt-1">
												{errors.password}
											</div>
										)}
									</div>
								</div>
								<div className="w-full mt-6">
									<Field name="agreement">
										{({ field }: { field: any }) => (
											<label className="grid gap-5 items-start">
												<div
													className={classnames(
														"self-stretch p-5 bg-white hover:bg-slate-100 rounded-[5px] outline outline-1 outline-offset-[-1px] inline-flex justify-start items-start gap-3 cursor-pointer transition-all duration-200 ease-in-out",
														{
															"outline-slate-200": !touched.agreement || !errors.agreement,
															"outline-king-red": touched.agreement && errors.agreement
														}
													)}
												>
													<input
														type="checkbox"
														{...field}
														checked={field.value}
														onChange={(e) => setFieldValue('agreement', e.target.checked)}
														className={classnames(
															"w-4 h-4 bg-white rounded-[5px] border",
															{
																"border-pge-blue": !touched.agreement || !errors.agreement,
																"border-king-red": touched.agreement && errors.agreement
															}
														)}
													/>
													<div className="flex-1 inline-flex flex-col justify-start items-start gap-3">
														<div className="text-zinc-700 text-xs font-normal font-['Inter']">
                                                            I agree to allow King Energy one-time direct access to my pge.com account for the purpose of authorizing associated utility account(s) on my behalf.
														</div>
														<div className="justify-start">
															<span className="text-zinc-700 text-xs font-normal font-['Inter']">
																Authorization Level:{" "}
															</span>
															<span className="text-zinc-700 text-xs font-semibold font-['Inter']">
																Full Access Until Cancelled
															</span>
														</div>
													</div>
												</div>
											</label>
										)}
									</Field>
									{errors.agreement && touched.agreement && (
										<div className="text-king-red text-xs font-normal font-['Inter'] mt-1">
											{errors.agreement}
										</div>
									)}
								</div>
							</UtilityPanel>
							<CorePanelFooter>
								<Button
									onClick={() => {
										const form = document.querySelector('form');
										if (form) {
											form.requestSubmit();
										}
									}}
									type="primary"
									className="w-full"
									isDisabled={!(isValid && dirty)}
								>
									Agree and Continue
								</Button>
								<Button
									onClick={onPrev}
									type="primary"
									subType="outline"
									className="w-full"
								>
									Back
								</Button>
							</CorePanelFooter>
						</Form>
					)}
				</Formik>
			</div>
		</CoreLayout>
	);
};

export default withErrorBoundary(Login);
